import { AlertLooks, StandardAlert, StandardLink, UseFetchOptions } from '@brandfolder/react';
import { Trans } from '@lingui/macro';
import React, { useRef, useState, FunctionComponent, useCallback, useEffect } from 'react';

import { AccessRequests } from '@components/bulk_management/user-settings/AccessRequests';
import { notAdminableAlertText } from '@components/bulk_management/user-settings/helpers';
import { InvitationMessageTabs } from '@components/bulk_management/user-settings/invitations';
import { PlanLimits } from '@components/bulk_management/user-settings/PlanLimits';
import {
  Brandfolder,
  Portal,
  ResourceDetails,
  ResourceSidebar,
  ServerOrganization
} from '@components/bulk_management/user-settings/resource-sidebar';
import { Tab, UserManagementTabs } from '@components/bulk_management/user-settings/UserManagementTabs';
import { I18nProviderWrapper } from '@components/common/I18nProviderWrapper';
import { defaultKnowledgeBaseLinks, gettyKnowledgeBaseLinks } from '@components/kb-article-links';
import { OnlyForScreenReader } from '@components/library/screen-reader';
import { isGettyClient } from '@helpers/getty-strings';
import { ListDropdown, ListOption } from '@components/library/dropdown';
import { fetchJson, useFetch } from '@api/ApiHelper';

import './styles/user-settings.scss';
import { ResourceUsageResponse } from '@components/limits/customLimitsTypes';

interface UserSettingsProps {
  brandfolders: Brandfolder[];
  organization: ServerOrganization;
  organizationAdmin: boolean;
  organizationOwner: boolean;
  portals?: Portal[];
  samlEnabledForDefaultUserPermission?: boolean;
}

export interface GetResourceDefaultUserPermission {
  resourceDefaultUserPermission: ResourceUsageResponse;
  fetchResourceDefaultUserPermission: (fetchDataOptions?: UseFetchOptions) => Promise<void>;
}

export const useGetResourceDefaultUserPermission = (
  resourceKey: string,
  resourceType = 'brandfolder',
): GetResourceDefaultUserPermission => {
  const resourceUrl =
    resourceType === 'portal'
      ? `/api/v4/private/portals/${resourceKey}?fields=default_user_permission`
      : `/api/v4/${resourceType}s/${resourceKey}?fields=default_user_permission`;

  const {
    response: resourceDefaultUserPermission,
    fetch: fetchResourceDefaultUserPermission
  } = useFetch<ResourceUsageResponse>({
    url: resourceUrl,
    fetchOnMount: false,
  });

  return { resourceDefaultUserPermission, fetchResourceDefaultUserPermission };
};

export const UserSettings: FunctionComponent<UserSettingsProps> = ({
  brandfolders,
  organization,
  organizationAdmin,
  organizationOwner,
  portals = [],
  samlEnabledForDefaultUserPermission = false,
}) => {
  const body = useRef<HTMLDivElement | null>(null);
  const [resource, setResource] = useState<ResourceDetails>({
    adminable: true, // being able to load into bulk management page means user is admin
    headingType: BFG.resource.type,
    key: BFG.resource.key,
    name: BFG.resource.name,
    type: BFG.resource.type === 'workspace' ? 'collection' : BFG.resource.type
  });
  const [reloadPlanLimits, setReloadPlanLimits] = useState(false);
  const [reloadUsers, setReloadUsers] = useState(false);
  const [reloadUserGroups, setReloadUserGroups] = useState(false);
  const [reloadPendingInvitations, setReloadPendingInvitations] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [currentTab, setCurrentTab] = useState<Tab>('users');
  const manageUsers = window.location.pathname.includes('/manage-users');
  const orgKey = organization?.key || BFG.organization_key;
  const {
    resourceDefaultUserPermission,
    fetchResourceDefaultUserPermission
  } = useGetResourceDefaultUserPermission(resource.key, resource.type);
  const customPermissionsEnabled = BFG.hasFeature('custom_permissions_rollout');

  const load = useCallback((): void => {
    fetchResourceDefaultUserPermission();
  }, [fetchResourceDefaultUserPermission]);

  const defaultUserPermission = Array.isArray(resourceDefaultUserPermission?.data) ? null :
    resourceDefaultUserPermission?.data.attributes?.default_user_permission;

  const handleDefaultUserPermissionChange = useCallback(async (selectedOption: ListOption): Promise<void> => {
    const permission = selectedOption.value;
    const resourceType = `${resource.type}s`;
    const resourceKey = resource.key;

    try {
      await fetchJson({
        url: `/api/v4/${resourceType === 'portals' ? 'private/' : ''}${resourceType}/${resourceKey}`,
        method: 'PUT',
        body: {
          data: {
            type: resourceType,
            id: resourceKey,
            attributes: {
              default_user_permission: permission
            }
          }
        }
      });

      Notify.create({
        title: 'Default Permission Saved',
        body: 'This setting has been saved for new users.',
        type: 'success'
      });

      fetchResourceDefaultUserPermission();
    } catch (error) {
      Notify.create({
        title: 'Error Saving Default Permission',
        body: 'The default permission was not saved. Try refreshing the page and setting the permission again.',
        type: 'error'
      });
    }
  }, [resource.type, resource.key, fetchResourceDefaultUserPermission]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgKey, resource]);

  // Track "All Users" page load with Amplitude
  useEffect(() => {
    if (!manageUsers && window.BFG?.amplitude?.enabled && window.amplitudeService) {
      window.amplitudeService.trackEvent('PageLoad', {
        elementName: 'PageLoad',
        elementLocation: 'UserManagementView',
        productArea: 'BrandfolderSettings',
        featureArea: 'UsersTab',
        eventName: 'PageLoad',
        'key properties': {}
      });
    }
  }, [manageUsers]);

  let knowledgeBaseUrl;

  if (isGettyClient()) {
    knowledgeBaseUrl = gettyKnowledgeBaseLinks.userPermissionsLink;
  } else {
    knowledgeBaseUrl = defaultKnowledgeBaseLinks.userPermissionsLink;
  }

  let bodyContent;

  if (!resource.adminable) {
    bodyContent = (
      <div className="user-settings__not-adminable-alert">
        <StandardAlert
          heading={resource.name}
          look={AlertLooks.Error}
        >
          {notAdminableAlertText()[resource.type]}
        </StandardAlert>
      </div>
    );
  } else if (!manageUsers && resource.type === 'organization') {
    bodyContent = (
      <div className="user-settings__organization-alert">
        <StandardAlert
          heading={resource.name}
          look={AlertLooks.Warning}
        >
          <StandardLink
            href={customPermissionsEnabled ? `/organizations/${organization.slug}/manage-users` : `/organizations/${organization.slug}#!manage_users`}
          >
            <Trans>Invite users at the Organization level.</Trans>
          </StandardLink>
        </StandardAlert>
      </div>
    );
  } else {
    bodyContent = (
      <div ref={body} className="user-settings__main">
        {/* Portals do not support access requests */}
        {(resource.type !== 'portal' && resource.type !== 'organization') && (
          <AccessRequests
            selectedResourceKey={resource.key}
            selectedResourceType={resource.type}
            setReloadPendingInvitations={setReloadPendingInvitations}
            setReloadPlanLimits={setReloadPlanLimits}
            setReloadUsers={setReloadUsers}
          />
        )}
        <div className="user-settings__segment">
          <PlanLimits
            brandfolders={brandfolders}
            orgKey={organization?.key || BFG.organization_key}
            organizationAdmin={organizationAdmin}
            organizationOwner={organizationOwner}
            reload={reloadPlanLimits}
            resource={resource}
            setReload={setReloadPlanLimits}
            setReloadUsers={setReloadUsers}
          />
        </div>
        <InvitationMessageTabs
          brandfolders={brandfolders}
          portals={portals}
          reloadPendingInvitations={reloadPendingInvitations}
          selectedResourceKey={resource.key}
          selectedResourceType={resource.type}
          setReloadPendingInvitations={setReloadPendingInvitations}
          setReloadPlanLimits={setReloadPlanLimits}
          setReloadUserGroups={setReloadUserGroups}
          setReloadUsers={setReloadUsers}
        />
        <UserManagementTabs
          brandfolders={brandfolders}
          currentTab={currentTab}
          reloadUserGroups={reloadUserGroups}
          reloadUsers={reloadUsers}
          resourceName={resource.name}
          selectedExtendedResourceType={resource.headingType}
          selectedResourceKey={resource.key}
          selectedResourceType={resource.type}
          setCurrentTab={setCurrentTab}
          setReloadPlanLimits={setReloadPlanLimits}
          setReloadUserGroups={setReloadUserGroups}
          setReloadUsers={setReloadUsers}
          setUsersLoaded={setUsersLoaded}
        />
      </div>
    );
  }

  return (
    <I18nProviderWrapper>
      <div className="user-settings">
        {!manageUsers ? (
          <>
            <h2>
              <Trans>All Users</Trans>
            </h2>
            <p className="user-settings__permissions-blurb">
              {isGettyClient()
                ? <Trans>Administrators have the ability to invite users to Library resources.</Trans>
                : <Trans>Administrators have the ability to invite users to Brandfolder resources.</Trans>}&nbsp;
              <Trans>This seamless process invites users to access certain resources at any permission level.</Trans>&nbsp;
              <StandardLink
                href={knowledgeBaseUrl}
                target="_blank"
              >
                <Trans>Learn more about user permissions.</Trans>
                <OnlyForScreenReader><Trans>Opens in a new tab.</Trans></OnlyForScreenReader>
              </StandardLink>
            </p>
          </>
        ) : (
          <>
            <div className="user-settings__header-row">
              <h2>
                <Trans>Organization Permissions for {organization.name}</Trans>
              </h2>
              {samlEnabledForDefaultUserPermission && (
                <div className="user-settings__permission-dropdown">
                  <ListDropdown
                    label="Default Permission Level"
                    onChange={handleDefaultUserPermissionChange}
                    options={[
                      { label: 'None', value: '' },
                      { label: 'Guest', value: 'guest' },
                      { label: 'Collaborator', value: 'collaborator' }
                    ]}
                    value={defaultUserPermission || ''}
                  />
                </div>
              )}
            </div>
            <p className="user-settings__permissions-blurb">
              {isGettyClient()
                ? <Trans>Administrators have the ability to invite users to Library resources.</Trans>
                : <Trans>Administrators have the ability to invite users to Brandfolder resources.</Trans>}&nbsp;
              <Trans>This seamless process invites users to access certain resources at any permission level.</Trans>&nbsp;
              <StandardLink
                href={knowledgeBaseUrl}
                target="_blank"
              >
                <Trans>Learn more about user permissions.</Trans>
                <OnlyForScreenReader><Trans>Opens in a new tab.</Trans></OnlyForScreenReader>
              </StandardLink>
            </p>
          </>
        )}
        <div className="user-settings__body">
          <div className="user-settings__sidebar">
            <ResourceSidebar
              bodyRef={body}
              brandfolders={brandfolders}
              organization={{
                ...organization,
                adminable: organizationAdmin,
                privacy: 'private',
              }}
              portals={portals}
              selectedResourceKey={resource.key}
              setResource={setResource}
              usersLoaded={usersLoaded}
            />
            </div>
            {bodyContent}
          </div>
        </div>
      </I18nProviderWrapper>
  );
};
